/**
 * Work Pass Application Form (Form B) — declarative field schema.
 *
 * This is the single source of truth for the MOM Work Pass Application Form
 * collected from WP / SP / EP candidates during pre-onboarding. The same schema
 * drives:
 *   - the HR-facing review/prefill panel on the Onboarding board (admin theme), and
 *   - the candidate-facing fill experience on the magic-link portal (employee theme).
 *
 * Form A (the general employment application) lives in the ATS and is NOT modelled
 * here. At handoff, whatever Form A captured is carried over and prefills the
 * matching fields below; the candidate only completes the remainder. Which fields
 * actually prefill is supplied at runtime via `prefilledKeys`, so this schema stays
 * stable while the Form A -> Form B mapping is finalised.
 *
 * Field shape:
 *   key            string   unique, snake_case; matches the backend serializer field
 *   label          string   shown to the user
 *   type           enum     'text' | 'email' | 'tel' | 'date' | 'select' | 'textarea'
 *   required       bool     whether MOM requires it (drives validation + the * marker)
 *   sensitive      bool     PDPA-sensitive (race/religion/marital/passport) — surfaced
 *                           with a subtle marker and purged for non-joiners
 *   options        array    [{ value, label }] for type 'select'
 *   placeholder    string   input placeholder
 *   hint           string   helper text under the field
 *   autoComplete   string   HTML autocomplete token (a11y / UX)
 *   colSpan        1 | 2    grid span within the section (default 1)
 *
 * Keep this file presentational-only: no React, no API calls.
 */

import { RELIGION_OPTIONS } from '../../../constants/religionOptions';
import { MARITAL_STATUS_OPTIONS } from '../../../constants/maritalStatusOptions';

export { RELIGION_OPTIONS, MARITAL_STATUS_OPTIONS };

export const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

// CMIO + Others, aligned with how race is captured for MOM filings.
export const RACE_OPTIONS = [
  { value: 'chinese', label: 'Chinese' },
  { value: 'malay', label: 'Malay' },
  { value: 'indian', label: 'Indian' },
  { value: 'eurasian', label: 'Eurasian' },
  { value: 'others', label: 'Others' },
];

export const QUALIFICATION_OPTIONS = [
  { value: 'secondary', label: 'Secondary' },
  { value: 'post_secondary', label: 'Post-secondary (A-levels / ITE)' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'degree', label: 'Bachelor’s degree' },
  { value: 'masters', label: 'Master’s degree' },
  { value: 'doctorate', label: 'Doctorate (PhD)' },
  { value: 'others', label: 'Others' },
];

/**
 * The form is organised into sections. Sections render as <fieldset> with a
 * <legend>, in order. Each section may declare `description` for context.
 */
export const WORK_PASS_APPLICATION_SCHEMA = [
  {
    key: 'personal_particulars',
    title: 'Personal Particulars',
    description: 'As shown on your passport. These details are submitted to MOM.',
    fields: [
      {
        key: 'full_name',
        label: 'Full name (as per passport)',
        type: 'text',
        required: true,
        autoComplete: 'name',
        colSpan: 2,
      },
      {
        key: 'preferred_name',
        label: 'Preferred / known-as name',
        type: 'text',
        required: false,
        autoComplete: 'nickname',
      },
      {
        key: 'date_of_birth',
        label: 'Date of birth',
        type: 'date',
        required: true,
        autoComplete: 'bday',
      },
      {
        key: 'gender',
        label: 'Gender',
        type: 'select',
        required: true,
        options: GENDER_OPTIONS,
      },
      {
        key: 'nationality',
        label: 'Nationality / citizenship',
        type: 'text',
        required: true,
        autoComplete: 'country-name',
      },
      {
        key: 'country_of_birth',
        label: 'Country of birth',
        type: 'text',
        required: true,
      },
      {
        key: 'country_of_origin',
        label: 'Country of origin',
        type: 'text',
        required: false,
        hint: 'Where you are applying from, if different to your country of birth.',
      },
      {
        key: 'race',
        label: 'Race',
        type: 'select',
        required: true,
        sensitive: true,
        options: RACE_OPTIONS,
      },
      {
        key: 'religion',
        label: 'Religion',
        type: 'select',
        required: true,
        sensitive: true,
        options: RELIGION_OPTIONS,
      },
      {
        key: 'marital_status',
        label: 'Marital status',
        type: 'select',
        required: true,
        sensitive: true,
        options: MARITAL_STATUS_OPTIONS,
      },
    ],
  },
  {
    key: 'passport',
    title: 'Passport',
    description: 'Your passport must be valid for the duration of the work pass.',
    fields: [
      {
        key: 'passport_number',
        label: 'Passport number',
        type: 'text',
        required: true,
        sensitive: true,
        autoComplete: 'off',
      },
      {
        key: 'passport_issue_date',
        label: 'Passport issue date',
        type: 'date',
        required: true,
      },
      {
        key: 'passport_expiry_date',
        label: 'Passport expiry date',
        type: 'date',
        required: true,
      },
    ],
  },
  {
    key: 'contact',
    title: 'Contact',
    description: 'How HR will reach you during onboarding.',
    fields: [
      {
        key: 'personal_email',
        label: 'Personal email',
        type: 'email',
        required: true,
        autoComplete: 'email',
        colSpan: 2,
      },
      {
        key: 'personal_mobile',
        label: 'Mobile number',
        type: 'tel',
        required: true,
        autoComplete: 'tel',
        placeholder: '+65 9XXX XXXX',
      },
      {
        key: 'residential_address',
        label: 'Residential address (home country)',
        type: 'textarea',
        required: true,
        colSpan: 2,
      },
      {
        key: 'local_address',
        label: 'Local address in Singapore (if any)',
        type: 'textarea',
        required: false,
        colSpan: 2,
        hint: 'Leave blank if you do not yet have a Singapore address.',
      },
    ],
  },
  {
    key: 'education_employment',
    title: 'Education & Employment',
    description: 'Used to support the work-pass application. Often prefilled from your application.',
    fields: [
      {
        key: 'highest_qualification',
        label: 'Highest qualification',
        type: 'select',
        required: true,
        options: QUALIFICATION_OPTIONS,
      },
      {
        key: 'field_of_study',
        label: 'Field / discipline of study',
        type: 'text',
        required: false,
      },
      {
        key: 'institution',
        label: 'Institution name',
        type: 'text',
        required: false,
        colSpan: 2,
      },
      {
        key: 'year_of_graduation',
        label: 'Year of graduation',
        type: 'text',
        required: false,
        placeholder: 'YYYY',
      },
      {
        key: 'years_of_experience',
        label: 'Relevant years of experience',
        type: 'text',
        required: false,
      },
      {
        key: 'previous_employer',
        label: 'Most recent employer',
        type: 'text',
        required: false,
        colSpan: 2,
      },
    ],
  },
];

/** Flattened [{...field, sectionKey}] for validation / lookups. */
export const ALL_WORK_PASS_FIELDS = WORK_PASS_APPLICATION_SCHEMA.flatMap((section) =>
  section.fields.map((f) => ({ ...f, sectionKey: section.key }))
);

/** Keys of every required field — used to compute completion + validation. */
export const REQUIRED_FIELD_KEYS = ALL_WORK_PASS_FIELDS.filter((f) => f.required).map(
  (f) => f.key
);

/**
 * Map a stored value to its human label for read-only / review rendering.
 * Falls back to the raw value for free-text fields.
 */
export const labelForValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '';
  if (field.type === 'select' && Array.isArray(field.options)) {
    const match = field.options.find((o) => String(o.value) === String(value));
    return match ? match.label : String(value);
  }
  return String(value);
};
